import { Story, Chapter, Step, StepSection, Branding, StoryMedia } from '@app/components/Risk/types'
import { defaultBranding } from '../../Stories.utils'

/**
 * Local story extends the base Story type with additional metadata
 * for tracking local-only stories that aren't persisted to the backend
 */
export interface LocalStory extends Story {
  isLocal: true
  createdAt: string
  updatedAt: string
}

/**
 * Union type for stories that can be either server-fetched or local
 */
export type MergedStory = Story | LocalStory

/**
 * Type guard to check if a story is a local story
 */
export function isLocalStory(story: MergedStory): story is LocalStory {
  return 'isLocal' in story && story.isLocal === true
}

/**
 * Input type for creating a new local story (without id and timestamps)
 */
export type CreateLocalStoryInput = Omit<Story, 'id'> & {
  visibility: 'Private' | 'Public'
}

/**
 * Input type for updating an existing local story
 */
export type UpdateLocalStoryInput = Partial<Omit<LocalStory, 'id' | 'isLocal' | 'createdAt'>>

/**
 * Default empty story template for the builder
 */
export const createEmptyStory = (): CreateLocalStoryInput => ({
  title: '',
  description: '',
  coverImageUrl: '',
  chapters: [createEmptyChapter()],
  visibility: 'Private',
  branding: defaultBranding,
  summary: '',
  basemap: 'Street',
  elements: [],
  scenarios: [],
  contexts: [],
  regions: [],
})

/**
 * Create an empty chapter template
 */
export const createEmptyChapter = (): Chapter => ({
  title: '',
  description: '',
  steps: [createEmptyStep()],
  basemap: undefined,
  elements: undefined,
  scenarios: undefined,
  contexts: undefined,
  regions: undefined,
})

/**
 * Create an empty step template
 */
export const createEmptyStep = (): Step => ({
  title: '',
  sections: [createEmptySection()],
  basemap: undefined,
  elements: undefined,
  scenarios: undefined,
  contexts: undefined,
  regions: undefined,
  location: undefined,
  media: undefined,
})

/**
 * Create an empty section template
 */
export const createEmptySection = (): StepSection => ({
  header: '',
  content: '',
  wideImage: undefined,
  sideImage: undefined,
  videoUrl: undefined,
  embedUrl: undefined,
  embedHeight: undefined,
  buttons: undefined,
})

/**
 * Create an empty media item
 */
export const createEmptyMedia = (): StoryMedia => ({
  location: {
    latitude: 0,
    longitude: 0,
  },
  activate: false,
  slides: [],
})

/**
 * Local storage key for stories
 */
export const LOCAL_STORIES_STORAGE_KEY = 'risk_local_stories'

/**
 * Starting ID for local stories (negative to avoid conflicts with server IDs)
 */
export const LOCAL_STORY_ID_START = -1
